"use client";

import React from "react";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const instruments = [{
  id: "01",
  title: "Working Capital",
  description: "High-velocity liquidity designed to stabilize operations and accelerate expansion without equity dilution.",
  advantages: ["Liquidity for operational expansion", "Inventory & supply chain financing", "Non-dilutive growth capital", "Revolving credit facilities"],
  link: "/BusinessCapitalPage"
}, {
  id: "02",
  title: "Term Loans",
  description: "Structured debt instruments offering fixed-rate stability and extended amortization for capital-intensive initiatives.",
  advantages: ["Fixed-rate capital structures", "Long-term amortization horizons", "Asset-backed securitization", "Equipment & capex financing"],
  link: "/BusinessCapitalPage"
}, {
  id: "03",
  title: "Private Credit",
  description: "Bespoke institutional lending solutions for complex transactions requiring speed, flexibility, and higher leverage.",
  advantages: ["Direct institutional lending", "Mezzanine & subordinated debt", "Bespoke covenant structures", "Acquisition & buyout financing"],
  link: "/RealEstateCapitalPage"
}, {
  id: "04",
  title: "Rental Portfolio Financing",
  description: "Institutional-grade capital structuring designed for Rental Portfolio Financing requirements.",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "05",
  title: "Bridge & Transitional Capital",
  description: "Institutional-grade capital structuring designed for Bridge & Transitional Capital requirements.",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "06",
  title: "Fix & Flip Capital",
  description: "Institutional-grade capital structuring designed for Fix & Flip Capital requirements.",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "07",
  title: "Construction & Development Capital",
  description: "Institutional-grade capital structuring designed for Construction & Development Capital requirements.",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "08",
  title: "Credit & Financing Protection",
  description: "Institutional-grade capital structuring designed for Credit & Financing Protection requirements.",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "09",
  title: "Strategic Capital Structuring",
  description: "Institutional-grade capital structuring designed for Strategic Capital Structuring requirements.",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "10",
  title: "Rental Portfolio Financing",
  description: "",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "11",
  title: "Bridge & Transitional Capital",
  description: "",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "12",
  title: "Fix & Flip Capital",
  description: "",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "13",
  title: "Construction & Development Capital",
  description: "",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "14",
  title: "Business Working Capital Solutions",
  description: "",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "15",
  title: "Structured Term Loans",
  description: "",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "16",
  title: "Startup & Emerging Business Funding",
  description: "",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "17",
  title: "Unsecured Funding Programs",
  description: "",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "18",
  title: "Industry-Specific Funding",
  description: "",
  advantages: [],
  link: "/BusinessCapitalPage"
}, {
  id: "19",
  title: "Nonprofit Line of Credit",
  description: "",
  advantages: [],
  link: "/BusinessCapitalPage"
}];
export default function CapitalInstruments() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="capital-instruments" ref={ref} className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--color-border)_0%,_transparent_30%)] opacity-10 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-20" />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-24 gap-x-12">
          
          {}
          <div className={`lg:col-span-7 flex flex-col gap-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} data-index={0}>
            <div className="relative pl-4 border-l border-border/30">
              <span className="absolute -top-16 left-0 text-[8rem] leading-none font-serif font-bold opacity-30 select-none" style={{
              WebkitTextStroke: '1px var(--color-border)',
              color: 'transparent'
            }}>
                {instruments[0].id}
              </span>
              <div className="relative pt-12">
                <h3 className="text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-6">
                  {instruments[0].title}
                </h3>
                <p className="text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
                  {instruments[0].description}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                  {instruments[0].advantages.map((advantage, i) => <div key={i} className="flex items-start gap-3 group" data-index={i}>
                      <div className="mt-1 p-0.5 rounded-full border border-border/50 group-hover:border-primary/50 transition-colors">
                        <Check className="w-3 h-3 text-primary" />
                      </div>
                      <span className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                        {advantage}
                      </span>
                    </div>)}
                </div>

                <Button asChild variant="outline" className="rounded-none border-border hover:bg-accent hover:text-accent-foreground transition-all uppercase tracking-widest text-xs h-12 px-8">
                  <Link to={instruments[0].link}>
                    Explore Structure <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-5 lg:col-start-8 lg:mt-32 flex flex-col gap-8 transition-all duration-1000 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} data-index={1}>
            <div className="relative pl-4 border-l border-border/30">
              <span className="absolute -top-16 left-0 text-[8rem] leading-none font-serif font-bold opacity-30 select-none" style={{
              WebkitTextStroke: '1px var(--color-border)',
              color: 'transparent'
            }}>
                {instruments[1].id}
              </span>
              <div className="relative pt-12">
                <h3 className="text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-6">
                  {instruments[1].title}
                </h3>
                <p className="text-lg text-muted-foreground max-w-md mb-8 leading-relaxed">
                  {instruments[1].description}
                </p>
                
                <div className="flex flex-col gap-4 mb-10">
                  {instruments[1].advantages.map((advantage, i) => <div key={i} className="flex items-center gap-3 group" data-index={i}>
                      <div className="p-0.5 rounded-full border border-border/50 group-hover:border-primary/50 transition-colors">
                        <Check className="w-3 h-3 text-primary" />
                      </div>
                      <span className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                        {advantage}
                      </span>
                    </div>)}
                </div>

                <Button asChild variant="outline" className="rounded-none border-border hover:bg-accent hover:text-accent-foreground transition-all uppercase tracking-widest text-xs h-12 px-8">
                  <Link to={instruments[1].link}>
                    View Terms <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-8 lg:col-start-2 lg:mt-12 flex flex-col gap-8 transition-all duration-1000 delay-400 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} data-index={2}>
            <div className="relative pl-4 border-l border-border/30">
              <span className="absolute -top-16 left-0 text-[8rem] leading-none font-serif font-bold opacity-30 select-none" style={{
              WebkitTextStroke: '1px var(--color-border)',
              color: 'transparent'
            }}>
                {instruments[2].id}
              </span>
              <div className="relative pt-12">
                <h3 className="text-4xl md:text-5xl font-medium tracking-tight text-foreground mb-6">
                  {instruments[2].title}
                </h3>
                <p className="text-lg text-muted-foreground max-w-2xl mb-8 leading-relaxed">
                  {instruments[2].description}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 mb-10">
                  {instruments[2].advantages.map((advantage, i) => <div key={i} className="flex items-center gap-3 group border-b border-border/10 pb-2" data-index={i}>
                      <div className="p-0.5 rounded-full border border-border/50 group-hover:border-primary/50 transition-colors">
                        <Check className="w-3 h-3 text-primary" />
                      </div>
                      <span className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                        {advantage}
                      </span>
                    </div>)}
                </div>

                <Button asChild variant="outline" className="rounded-none border-border hover:bg-accent hover:text-accent-foreground transition-all uppercase tracking-widest text-xs h-12 px-8 w-fit">
                  <Link to={instruments[2].link}>
                    Access Private Credit <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}